// src/lib/captures/labels.ts
import type { CapturePhase } from "./capture-phase";
import type { Capture, CaptureType } from "./schema";

export const AUDIO_RETENTION_DAYS = 14;

// Mention sobre affichée quand l'audio d'une capture a été supprimé (source
// unique partagée par la carte d'accueil et la page dédiée — évite la dérive
// de formulation entre les deux emplacements).
export const AUDIO_DELETED_NOTICE = "Audio supprimé (protection des renseignements personnels)";

function frDate(iso: string, timeZone?: "UTC"): string {
  return new Date(iso).toLocaleDateString("fr-CA", { day: "numeric", month: "long", timeZone });
}

/** Date du titre par défaut : jour et mois ENTIER (« 24 septembre »), heure locale. */
export function formatTitleDate(iso: string): string {
  return frDate(iso);
}

/** Heure du titre par défaut (« 13h42 »), heure locale. */
export function formatTitleTime(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })
    .replace(/\s*[h:]\s*/i, "h");
}

// `dateOnly` : un import n'a pas d'heure réelle (date du formulaire stockée à
// minuit UTC) — le titre par défaut porte la date seule, lue en UTC, sans le
// « à 20h00 » fantôme (minuit UTC affiché en heure de Montréal — retour QA).
export function formatDefaultCaptureTitle(
  type: CaptureType,
  recordedAtIso: string,
  opts?: { dateOnly?: boolean },
): string {
  const noun = type === "rencontre" ? "Rencontre" : "Idée";
  if (opts?.dateOnly) return `${noun} du ${frDate(recordedAtIso, "UTC")}`;
  return `${noun} du ${formatTitleDate(recordedAtIso)} à ${formatTitleTime(recordedAtIso)}`;
}

export function captureDisplayTitle(
  capture: Pick<Capture, "title" | "type" | "recordedAt" | "durationS" | "audioPath">,
): string {
  return (
    capture.title ??
    formatDefaultCaptureTitle(capture.type, capture.recordedAt, {
      dateOnly: captureNeverHadAudio(capture),
    })
  );
}

// La capture a-t-elle un titre propre, ou retombe-t-elle sur le titre par défaut
// (type + date + heure) ? Le défaut portant déjà type et date, on s'appuie sur ce
// test pour ne pas les répéter dans la ligne de métadonnées sous le titre.
export function captureHasOwnTitle(capture: Pick<Capture, "title">): boolean {
  return capture.title != null;
}

export function retentionDeadlineIso(recordedAtIso: string, days = AUDIO_RETENTION_DAYS): string {
  return new Date(new Date(recordedAtIso).getTime() + days * 86_400_000).toISOString();
}

export function formatRetentionDeadline(
  recordedAtIso: string,
  days = AUDIO_RETENTION_DAYS,
): string {
  return frDate(retentionDeadlineIso(recordedAtIso, days));
}

// Distingue « audio jamais existé » (import d'une transcription) de « audio
// purgé » (enregistrement après rétention) : un import crée la capture avec
// durationS=0 et audioPath=null (aucune mesure de durée possible, il n'y a
// jamais eu de fichier). Les DEUX conditions sont exigées : un enregistrement
// réel dont la durée best-effort vaut 0 (échec de mesure) mais qui GARDE son
// audioPath reste traité comme un enregistrement normal (son lecteur reste
// disponible via la page dédiée) — dégradation mineure acceptée plutôt que de
// risquer une fausse mention « jamais eu d'audio » sur un vrai enregistrement.
export function captureNeverHadAudio(capture: Pick<Capture, "durationS" | "audioPath">): boolean {
  return capture.durationS === 0 && !capture.audioPath;
}

// A-t-on quelque chose à dire du SORT de l'audio (lecteur si présent, mention
// « Audio supprimé » sinon) ? Faux pour un import (jamais eu d'audio) ET pour
// une capture `preparing`/`problem` (A7, correctif #6) : `audio_path` y est
// NUL par construction (contrainte `keep_audio_coherent`, l'audio n'est pas
// encore rapatrié ou l'assemblage a échoué) — ce n'est PAS une suppression, le
// bandeau d'état de la fiche le dit déjà. Prédicat partagé par la liste
// (CapturesList) et la fiche (onglet Contexte) : un seul endroit décide QUAND
// parler du sort de l'audio.
export function captureShowsAudioFate(
  capture: Pick<Capture, "durationS" | "audioPath">,
  phase: CapturePhase,
): boolean {
  return !captureNeverHadAudio(capture) && phase !== "preparing" && phase !== "problem";
}

// Date affichée d'une capture (carte d'accueil + onglet Contexte). Un import
// n'a PAS d'heure réelle : le formulaire ne demande qu'une date, stockée à
// minuit UTC (`new Date("YYYY-MM-DD")`, ImportTranscriptScreen). Afficher
// l'heure locale décalait donc d'un jour (« 21 juill., 20 h 00 » pour un
// import daté du 22, retour QA) — on affiche la DATE SEULE, lue en UTC pour
// restituer le jour choisi. Un enregistrement garde date + heure locales.
export function formatCaptureDate(
  capture: Pick<Capture, "durationS" | "audioPath" | "recordedAt">,
): string {
  if (captureNeverHadAudio(capture)) {
    return new Date(capture.recordedAt).toLocaleDateString("fr-CA", {
      dateStyle: "medium",
      timeZone: "UTC",
    });
  }
  return new Date(capture.recordedAt).toLocaleString("fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function captureRetentionNotice(
  capture: Pick<Capture, "status" | "recordedAt">,
): string | null {
  if (capture.status !== "captured") return null;
  return `Audio supprimé le ${formatRetentionDeadline(capture.recordedAt)} si non analysé`;
}

// Texte de l'infobulle du lecteur audio (DS §6.14) : le SORT de l'audio, en
// clair. La ligne courte ci-dessus n'annonce qu'une échéance ; ici on dit la
// règle entière, telle que `purge-audio` l'applique vraiment :
//   - l'audio de TOUTE capture échue est purgé à J+14 (le select de la purge
//     ne filtre pas sur le statut) ;
//   - une capture restée « captured » (transcript_raw nul) est en plus
//     SUPPRIMÉE en entier, ligne comprise (deleteNeverAnalyzedCaptures).
// Toujours une phrase (jamais null) : le lecteur n'existe que si l'audio est
// encore là, et dans ce cas il y a toujours quelque chose à dire — un trou
// laisserait la QA chercher une infobulle absente (retour 25-08).
export function captureAudioFateExplanation(
  capture: Pick<Capture, "status" | "recordedAt">,
): string {
  const echeance = formatRetentionDeadline(capture.recordedAt);
  const delai = `${AUDIO_RETENTION_DAYS} jours après l'enregistrement`;
  if (capture.status === "captured") {
    return `L'audio est conservé jusqu'au ${echeance}, soit ${delai}. Sans transcription ni analyse d'ici là, l'audio ET la capture sont supprimés — il ne reste rien. Une fois l'analyse lancée, la transcription et les sujets restent, même quand l'audio part.`;
  }
  return `L'audio est supprimé le ${echeance}, soit ${delai}. Ce qui en a été tiré — transcription, sujets — reste : c'est l'audio seul qui part.`;
}
